"use client"
import { useEffect, useState } from 'react'
import { type ScenarioEnding } from '../../ScenarioEndFooter'
import { EPILOGUE_HEADING_ID, POV_HEADING_IDS } from './planATimelineDiagramModel'

// The one place that answers "which Plan A ending is open right now?" for the
// timeline diagram and its end-cap. All three endings (Epilogue / Public POV /
// Insider POV) are expanded by routing through the URL hash — the same channel
// useAnchorDeepLink already turns into mount-and-scroll — so the live hash IS the
// single source of truth for the current selection, across every context the
// diagram renders in: the main Plan A page, the inline Plan A continuation, and
// the standalone `?perspective=` flat pages.
//
// Returns null when no ending is open (the reader is on the main scenario hub).
// 'mainScenario' is deliberately NOT a value here — it means "no ending selected"
// and is represented by null, so callers can treat "something is open" as truthy.
export type SelectedEnding = Exclude<ScenarioEnding, 'mainScenario'>

// Hash id → the ending it opens. Built from the same id constants the navigation
// and deep-link code use, so the mapping can't drift from them.
const ENDING_BY_HASH_ID: Record<string, SelectedEnding> = {
  [EPILOGUE_HEADING_ID]: 'epilogue',
  [POV_HEADING_IDS.public]: 'public',
  [POV_HEADING_IDS.insider]: 'insider',
}

const endingFromHash = (): SelectedEnding | null => {
  if (typeof window === 'undefined') return null
  const key = window.location.hash.replace(/^#/, '')
  return ENDING_BY_HASH_ID[key] ?? null
}

// Live-updating selected ending. Re-reads on every hashchange (the same event
// the diagram's own navigation fires when it opens an ending), so the highlight
// and the end-cap react in lockstep with what's expanded below.
export const useSelectedEnding = (): SelectedEnding | null => {
  const [ending, setEnding] = useState<SelectedEnding | null>(null)

  useEffect(() => {
    const sync = () => setEnding(endingFromHash())
    sync() // initial: a deep-linked load already has the hash set
    window.addEventListener('hashchange', sync)
    return () => window.removeEventListener('hashchange', sync)
  }, [])

  return ending
}
